/**
 * Privacy Policy page — public, unauthenticated route.
 * Verified production disclosures; keep aligned with deployed infrastructure.
 */
import React from "react";
import { Link } from "react-router-dom";
import LegalPageShell from "./LegalPageShell";

const H2 = ({ children }) => (
  <h2 className="mt-8 text-[18px] font-bold text-[#172033]">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-3 text-[14px] leading-relaxed text-[#3E4245]">{children}</p>
);
const UL = ({ children }) => (
  <ul className="mt-3 list-disc pl-5 text-[14px] leading-relaxed text-[#3E4245] space-y-1.5">{children}</ul>
);

const Privacy = () => (
  <LegalPageShell title="Privacy Policy" testid="privacy-page">
    <P>
      <strong>Effective date:</strong> 29 August 2026 &nbsp;·&nbsp;
      <strong>Last updated:</strong> 25 September 2026
    </P>

    <P>
      This policy explains what personal information FrontRow Parents collects when you use the service, how we use it, and the choices you have. It is written for parents, in plain English. If any wording is unclear, please contact us (see below).
    </P>

    <H2>Who runs FrontRow Parents</H2>
    <P>
      FrontRow Parents is operated by <strong>The Trustee for Zoozara Trust, trading as FrontRow Parents</strong> (ABN 13 117 155 108), based in New South Wales, Australia. In this policy, "FrontRow", "we", "us" and "our" refer to the operator.
    </P>

    <H2>Information we collect</H2>
    <P>When you sign in and connect a mailbox, FrontRow collects and stores the following, per family account:</P>
    <UL>
      <li><strong>Account identity from Clerk</strong> — the email address, name and Clerk user identifier you use to sign in.</li>
      <li><strong>Mailbox connection tokens</strong> — the OAuth access token and refresh token issued by Google (for Gmail) or Microsoft (for Outlook). Tokens are stored encrypted at rest using Fernet symmetric encryption; the key is held only in server environment configuration and is never returned to your browser.</li>
      <li><strong>Selected email content, on demand</strong> — for each message our background poller processes, we retrieve the message metadata (subject, sender, received date) and the message body needed to identify relevant school information.</li>
      <li><strong>Derived FrontRow data</strong> — after processing, we persist interpreted school information such as events, dates, locations, child relevance and actions, together with the normalised source information used so the dashboard can show provenance.</li>
      <li><strong>Your app activity</strong> — which items you dismissed, marked complete, acknowledged, added to calendar, and similar activity.</li>
    </UL>

    <H2>Google user data — Limited Use</H2>
    <P>
      FrontRow requests one Google API scope only:<br />
      <code className="text-[13px] bg-[#F2EFE7] px-1.5 py-0.5 rounded">https://www.googleapis.com/auth/gmail.readonly</code>
    </P>
    <P>
      Our use of information received from Google APIs adheres to the{" "}
      <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-[#4A5D4E] underline">Google API Services User Data Policy</a>, including the Limited Use requirements. Specifically:
    </P>
    <UL>
      <li>We use Gmail data <strong>only</strong> to provide and improve the user-facing feature of surfacing relevant school information on your FrontRow dashboard.</li>
      <li>We do <strong>not</strong> transfer Gmail data to others except as necessary to provide or improve the feature, to comply with applicable law, or as part of a merger, acquisition, or sale of assets with your notice.</li>
      <li>We do <strong>not</strong> use Gmail data to serve advertisements, including retargeting, personalised, or interest-based advertising.</li>
      <li>Humans do not read your Gmail data, except (a) with your explicit consent for a specific message, (b) when necessary for security purposes such as investigating abuse, (c) to comply with applicable law, or (d) where the data has been aggregated and anonymised for our internal operations.</li>
    </UL>

    <H2>How we process Gmail messages</H2>
    <P>
      To identify relevant school information, message subjects and body text are sent to a large-language-model provider currently OpenAI, with Anthropic available as a fallback provider for structured extraction. Only the minimum content needed for extraction is sent — never OAuth tokens, never your Clerk credentials. Attachments that contain images may be sent for OCR to the same provider. Providers process the content transiently to return structured information. See "Third parties" below.
    </P>

    <H2>How we store your data</H2>
    <UL>
      <li>Application data (families, children, events, actions, processed message bodies) is stored in a MongoDB database operated by us.</li>
      <li>Gmail and Outlook OAuth tokens are stored in a separate <code className="text-[13px] bg-[#F2EFE7] px-1 py-0.5 rounded mx-0.5">ee_email_tokens</code> collection, encrypted at rest with Fernet before persistence.</li>
      <li>Short-lived OAuth state records used to prevent CSRF during sign-in expire automatically via a database time-to-live index shortly after use.</li>
      <li>FrontRow's production application, backend and managed database are hosted in the United States (us-central1). Some service providers may process data in other countries as described under "Third parties (subprocessors)" below.</li>
    </UL>

    <H2>Retention and deletion</H2>
    <P>FrontRow retains the data listed above for as long as your account is active. You can:</P>
    <UL>
      <li><strong>Disconnect your mailbox</strong> at any time from the FrontRow Settings page. When you disconnect, the encrypted OAuth tokens for that provider are deleted from our database immediately. Google can additionally revoke FrontRow's access from your{" "}<a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-[#4A5D4E] underline">Google Account permissions page</a>.</li>
      <li><strong>Request full account deletion</strong> by emailing us (see "Contact" below). We aim to complete deletion within 30 days of your verified request.</li>
    </UL>

    <H2>Third parties (subprocessors)</H2>
    <UL>
      <li><strong>Clerk</strong> — authentication (sign-in, sign-up, session management). Receives your email address and name when you create a FrontRow account.</li>
      <li><strong>Google</strong> — Gmail API access. Receives OAuth requests from FrontRow using tokens you granted; returns message metadata and body content.</li>
      <li><strong>Microsoft</strong> — Outlook / Microsoft Graph API access, on the same basis as Google above, for parents who connect an Outlook mailbox.</li>
      <li><strong>AI service providers (currently OpenAI and Anthropic)</strong> — may receive the minimum email subject and body text needed to extract structured information. No OAuth tokens or Clerk credentials are sent to these providers.</li>
      <li><strong>Emergent</strong> — hosts FrontRow's production frontend, backend and managed database in the United States. Emergent also provides infrastructure and backup services.</li>
      <li><strong>Cloudflare</strong> — provides global network, security and content-delivery infrastructure for FrontRow.</li>
    </UL>

    <H2>Security</H2>
    <P>We take reasonable technical measures to protect the information we hold, including transport encryption (HTTPS) to and from FrontRow and encryption at rest for OAuth tokens. No internet service is completely secure — we cannot guarantee absolute security.</P>

    <H2>Children</H2>
    <P>FrontRow is intended for parents and guardians. Children do not create FrontRow accounts. School information may include your child's name; that information is stored in your family account and is visible only to people authorised for that child.</P>

    <H2>Australian consumer law</H2>
    <P>Nothing in this policy limits any rights you have under the Australian Consumer Law or the Privacy Act 1988 (Cth).</P>

    <H2>Changes to this policy</H2>
    <P>We may update this policy from time to time. Material changes will be notified in-app or by email before they take effect. The "Last updated" date at the top of this page always reflects the current version.</P>

    <H2>Contact</H2>
    <P>Questions, deletion requests, or complaints:{" "}<a href="mailto:support.frontrow@gmail.com" className="text-[#4A5D4E] underline">support.frontrow@gmail.com</a>.</P>

    <P className="mt-8">See also: <Link to="/terms" className="text-[#4A5D4E] underline">Terms of Service</Link>&nbsp;·&nbsp;<Link to="/sign-in" className="text-[#4A5D4E] underline">Sign in</Link></P>
  </LegalPageShell>
);

export default Privacy;
